import { AudioGarden } from './audio';
import { PhysicsGarden, PHYSICS_CONSTANTS } from './physics';
import { GardenRenderer, type AimVisual } from './renderer';
import { calculateStageScore, campaignRank, collectPetal, createInitialRun, MAX_PULSES } from './scoring';
import { getStage, STAGES } from './stages';
import { loadPlayerData, prefersReducedMotion, savePlayerData, updateBest, type PlayerData } from './storage';
import type { FailureCause, GameEvent, RunState, StageResult, Vec2 } from './types';
import type { GameUI } from './ui';
import type { ViewportService } from './viewport';

declare global {
  interface Window {
    __KINETIC_BLOOM__?: {
      getState: () => { phase: RunState['phase']; stage: number; score: number; rapierActive: boolean; seed: Vec2 | null; velocity: Vec2 | null; petals: number; pulses: number };
      start: () => void;
      retry: () => void;
      jumpToStage?: (index: number) => Promise<void>;
    };
  }
}

const MIN_PULL = .28;
const MAX_PULL = 3.2;
const MIN_LAUNCH_SPEED = 7.4;
const MAX_LAUNCH_SPEED = 14.8;

export class KineticBloomGame {
  private run: RunState = createInitialRun();
  private player: PlayerData = loadPlayerData();
  private physics: PhysicsGarden | null = null;
  private renderer: GardenRenderer | null = null;
  private readonly audio: AudioGarden;
  private frame = 0;
  private lastTime = performance.now();
  private draggingPointer: number | null = null;
  private lastPointerWorld: Vec2 | null = null;
  private aimAngle = -Math.PI * .22;
  private aimStrength = .68;
  private aim: AimVisual = { active: true, vector: { x: Math.cos(this.aimAngle), y: Math.sin(this.aimAngle) }, strength: this.aimStrength };
  private inputLocked = false;
  private firstVisit = !this.player.onboardingComplete;
  private disposed = false;

  constructor(private readonly ui: GameUI, private readonly viewport: ViewportService) {
    this.audio = new AudioGarden(this.player.muted, this.player.haptics);
    this.applyPreferences();
    this.bindUI();
    const localQA = location.hostname === '127.0.0.1' || location.hostname === 'localhost';
    window.__KINETIC_BLOOM__ = {
      getState: () => ({
        phase: this.run.phase, stage: this.run.stageIndex + 1, score: this.run.score + this.run.stageScore, rapierActive: Boolean(this.physics),
        seed: this.physics?.position ?? null, velocity: this.physics?.velocity ?? null, petals: this.run.petalsCollected, pulses: this.run.pulses,
      }),
      start: () => this.beginCampaign(),
      retry: () => { void this.retry(); },
      ...(localQA ? { jumpToStage: async (index: number): Promise<void> => {
        if (!Number.isInteger(index) || index < 0 || index >= STAGES.length) throw new RangeError('Stage index must be between 0 and 4.');
        this.ui.hideTitle(); this.ui.hideOverlay(); this.ui.hideResult();
        this.run = { ...createInitialRun('loading'), stageIndex: index };
        await this.loadStage(index);
      } } : {}),
    };
  }

  async initialize(): Promise<void> {
    try {
      await this.loadStage(0, true);
      this.run = { ...this.run, phase: 'title' };
      this.ui.setPhase(this.run.phase);
      this.ui.showTitle(!this.firstVisit);
      this.frame = requestAnimationFrame(this.tick);
    } catch (error) {
      this.showLoadError(error);
    }
  }

  private readonly tick = (time: number): void => {
    if (this.disposed) return;
    const delta = Math.min(.1, Math.max(0, (time - this.lastTime) / 1000));
    this.lastTime = time;
    if (this.run.phase === 'playing' && this.physics) {
      const steps = this.physics.advance(delta, this.handleEvent);
      this.run = { ...this.run, elapsed: this.run.elapsed + steps * PHYSICS_CONSTANTS.fixedStep };
      this.consumePetals();
      this.ui.updateHUD(this.run, getStage(this.run.stageIndex));
    }
    if (this.renderer && this.physics) {
      this.renderer.render(this.physics.snapshot, this.aim, delta, this.run.phase === 'playing');
    }
    this.frame = requestAnimationFrame(this.tick);
  };

  private bindUI(): void {
    const canvas = this.ui.elements.canvas;
    canvas.addEventListener('pointerdown', this.onPointerDown);
    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('pointerup', this.onPointerUp);
    canvas.addEventListener('pointercancel', this.cancelDrag);
    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    document.getElementById('start-button')?.addEventListener('click', () => this.beginCampaign());
    this.ui.elements.pause.addEventListener('click', () => this.togglePause());
    this.ui.elements.mute.addEventListener('click', () => this.toggleMute());
    this.ui.elements.motion.addEventListener('click', () => this.cycleMotion());
    this.ui.elements.helpButton.addEventListener('click', () => this.openHelp());
    document.getElementById('help-close')?.addEventListener('click', () => this.ui.elements.help.close());
    this.ui.elements.help.addEventListener('cancel', (event) => { event.preventDefault(); this.ui.elements.help.close(); });
    this.ui.elements.overlay.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (target.closest('#resume-button')) this.resume();
      if (target.closest('#retry-button')) void this.retry();
    });
    this.ui.elements.result.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      if (target.closest('#continue-button')) void this.continueCampaign();
      if (target.closest('#replay-button')) void this.replay();
    });
  }

  private beginCampaign(): void {
    if (this.run.phase !== 'title' || this.inputLocked) return;
    void this.audio.activate();
    this.ui.hideTitle();
    this.player = { ...this.player, onboardingComplete: true };
    savePlayerData(this.player);
    this.run = { ...createInitialRun('ready'), stageIndex: 0 };
    this.ui.setPhase('ready');
    this.ui.updateHUD(this.run, getStage(0));
    this.ui.setStatus('Pull and release to launch', true);
    this.inputLocked = true;
    this.ui.showStageIntro(getStage(0), false, () => {
      this.inputLocked = false;
      this.ui.elements.canvas.focus();
    });
    this.aim = { active: true, vector: { x: Math.cos(this.aimAngle), y: Math.sin(this.aimAngle) }, strength: this.aimStrength };
    const hint = document.getElementById('gesture-hint');
    if (hint && this.firstVisit) hint.hidden = false;
    this.firstVisit = false;
  }

  private async loadStage(index: number, initial = false): Promise<void> {
    this.inputLocked = true;
    this.physics?.dispose();
    this.physics = null;
    const stage = getStage(index);
    this.viewport.setWorld(stage.world.width, stage.world.height);
    this.physics = await PhysicsGarden.initialize(stage);
    if (!this.renderer) this.renderer = new GardenRenderer(this.ui.elements.canvas, this.viewport, stage);
    else this.renderer.setStage(stage);
    this.renderer.setReducedEffects(prefersReducedMotion(this.player));
    if (!initial) {
      this.run = {
        ...this.run,
        phase: 'ready', stageIndex: index, stageScore: 0, combo: 0, maxCombo: 0, pulses: MAX_PULSES,
        retries: 0, elapsed: 0, petalsCollected: 0, lastPetalAt: Number.NEGATIVE_INFINITY, failure: null,
      };
      this.ui.setPhase('ready');
      this.ui.updateHUD(this.run, stage);
      this.ui.setStatus(stage.cue, true);
      this.ui.showStageIntro(stage, false, () => {
        this.inputLocked = false;
        this.ui.elements.canvas.focus();
      });
    }
    this.aim = { active: true, vector: { x: Math.cos(this.aimAngle), y: Math.sin(this.aimAngle) }, strength: this.aimStrength };
    if (initial) this.inputLocked = false;
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (this.inputLocked || !this.physics) return;
    void this.audio.activate();
    const point = this.eventWorldPoint(event);
    this.lastPointerWorld = point;
    if (this.run.phase === 'ready') {
      if (distance(point, this.physics.position) > .9) return;
      this.draggingPointer = event.pointerId;
      this.ui.elements.canvas.setPointerCapture(event.pointerId);
      this.aim = { active: true, vector: { ...this.aim.vector }, strength: 0 };
    } else if (this.run.phase === 'playing' && this.run.pulses > 0) {
      this.usePulse(toward(this.physics.position, point));
    }
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (!this.physics) return;
    const point = this.eventWorldPoint(event);
    this.lastPointerWorld = point;
    if (this.draggingPointer !== event.pointerId || this.run.phase !== 'ready') return;
    const pull = { x: this.physics.position.x - point.x, y: this.physics.position.y - point.y };
    const magnitude = Math.min(MAX_PULL, Math.hypot(pull.x, pull.y));
    if (magnitude > .001) this.aim = { active: true, vector: pull, strength: magnitude / MAX_PULL };
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    if (this.draggingPointer !== event.pointerId || !this.physics) return;
    this.ui.elements.canvas.releasePointerCapture(event.pointerId);
    this.draggingPointer = null;
    const magnitude = Math.hypot(this.aim.vector.x, this.aim.vector.y) * this.aim.strength;
    if (this.aim.strength * MAX_PULL >= MIN_PULL && magnitude > .01) this.launch(this.aim.vector, this.aim.strength);
    else this.aim = { ...this.aim, strength: this.aimStrength };
  };

  private readonly cancelDrag = (): void => { this.draggingPointer = null; };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat && (event.code === 'Space' || event.key.toLowerCase() === 'p' || event.key.toLowerCase() === 'r')) return;
    const key = event.key.toLowerCase();
    if (['arrowleft', 'arrowright', 'arrowup', 'arrowdown', 'a', 'd', 'w', 's', ' ', 'p', 'r', 'escape', '?'].includes(key)) event.preventDefault();
    if (key === '?' || (key === '/' && event.shiftKey)) { this.openHelp(); return; }
    if (this.inputLocked) return;
    if (key === 'p' || key === 'escape') { this.togglePause(); return; }
    if (key === 'r' && (this.run.phase === 'playing' || this.run.phase === 'ready' || this.run.phase === 'failed')) { void this.retry(); return; }
    if (this.run.phase === 'title' && (event.code === 'Space' || key === 'enter')) { this.beginCampaign(); return; }
    if (this.run.phase === 'failed' && (event.code === 'Space' || key === 'enter')) { void this.retry(); return; }
    if (this.run.phase === 'stage-complete' && (event.code === 'Space' || key === 'enter')) { void this.continueCampaign(); return; }
    if (this.run.phase === 'campaign-complete' && (event.code === 'Space' || key === 'enter')) { void this.replay(); return; }
    if (this.run.phase === 'paused' && event.code === 'Space') { this.resume(); return; }
    if (this.run.phase === 'ready') {
      if (key === 'arrowleft' || key === 'a') this.aimAngle -= .075;
      if (key === 'arrowright' || key === 'd') this.aimAngle += .075;
      if (key === 'arrowup' || key === 'w') this.aimStrength = Math.min(1, this.aimStrength + .055);
      if (key === 'arrowdown' || key === 's') this.aimStrength = Math.max(.18, this.aimStrength - .055);
      this.aimAngle = Math.max(-Math.PI * .48, Math.min(Math.PI * .1, this.aimAngle));
      this.aim = { active: true, vector: { x: Math.cos(this.aimAngle), y: Math.sin(this.aimAngle) }, strength: this.aimStrength };
      if (event.code === 'Space') this.launch(this.aim.vector, this.aim.strength);
    } else if (this.run.phase === 'playing' && event.code === 'Space' && this.physics) {
      const direction = this.lastPointerWorld ? toward(this.physics.position, this.lastPointerWorld) : this.aim.vector;
      this.usePulse(direction);
    }
  };

  private launch(direction: Vec2, strength: number): void {
    if (this.run.phase !== 'ready' || !this.physics) return;
    const unit = normalize(direction);
    const speed = MIN_LAUNCH_SPEED + (MAX_LAUNCH_SPEED - MIN_LAUNCH_SPEED) * clamp01(strength);
    this.physics.launch({ x: unit.x * speed, y: unit.y * speed });
    this.run = { ...this.run, phase: 'playing' };
    this.aim = { ...this.aim, active: false };
    this.ui.setPhase('playing');
    this.ui.setStatus('Seed in flight');
    const hint = document.getElementById('gesture-hint');
    if (hint) hint.hidden = true;
    this.dispatch({ type: 'launch', strength, position: this.physics.position });
  }

  private usePulse(direction: Vec2): void {
    if (this.run.phase !== 'playing' || !this.physics || this.run.pulses <= 0) return;
    this.physics.pulse(direction);
    const pulses = this.run.pulses - 1;
    this.run = { ...this.run, pulses };
    this.ui.updateHUD(this.run, getStage(this.run.stageIndex));
    this.ui.setStatus(pulses ? 'One pulse remains' : 'No pulses remain', true);
    this.dispatch({ type: 'pulse', position: this.physics.position, direction: normalize(direction), remaining: pulses });
  }

  private consumePetals(): void {
    if (!this.physics) return;
    for (const index of this.physics.consumeCollectedPetals()) {
      const stage = getStage(this.run.stageIndex);
      const outcome = collectPetal(this.run, this.run.elapsed);
      this.run = outcome.run;
      const remaining = Math.max(0, stage.requiredPetals - this.run.petalsCollected);
      const position = stage.petals[index] ?? this.physics.position;
      this.dispatch({ type: 'petal', position, combo: this.run.combo, points: outcome.award });
      this.ui.setStatus(remaining ? `${remaining} marked ${remaining === 1 ? 'petal' : 'petals'} remain` : 'Flower open — land softly', true);
    }
  }

  private readonly handleEvent = (event: GameEvent): void => {
    if (event.type === 'complete') { this.completeStage(); return; }
    if (event.type === 'fail') { this.fail(event.cause, event); return; }
    if (event.type === 'goal-locked') this.ui.setStatus(`${event.remaining} marked ${event.remaining === 1 ? 'petal' : 'petals'} still needed`, true);
    if (event.type === 'goal-rejected') this.ui.setStatus(`Landing too fast — ${event.speed.toFixed(1)} velocity`, true);
    this.dispatch(event);
  };

  private completeStage(): void {
    if (!this.physics || this.run.phase !== 'playing') return;
    const stage = getStage(this.run.stageIndex);
    const breakdown = calculateStageScore({
      petalScore: this.run.stageScore, maxCombo: this.run.maxCombo, elapsed: this.run.elapsed, parTime: stage.parTime,
      pulsesRemaining: this.run.pulses, retries: this.run.retries, landingSpeed: this.physics.speed, maxLandingSpeed: stage.goal.maxSpeed,
    });
    const bestUpdate = updateBest(this.player, stage.id, breakdown.total);
    this.player = bestUpdate.data;
    const result: StageResult = { stageId: stage.id, score: breakdown.total, time: this.run.elapsed, retries: this.run.retries, breakdown, isBest: bestUpdate.stageBest };
    const score = this.run.score + breakdown.total;
    const results = [...this.run.results, result];
    const final = this.run.stageIndex === STAGES.length - 1;
    this.run = { ...this.run, phase: final ? 'campaign-complete' : 'stage-complete', score, stageScore: 0, results };
    this.ui.setPhase(this.run.phase);
    this.ui.updateHUD(this.run, stage);
    const event: GameEvent = { type: 'complete', position: this.physics.position, perfect: this.physics.speed < stage.goal.maxSpeed * .5 };
    this.dispatch(event);
    if (final) {
      const campaignUpdate = updateBest(this.player, stage.id, breakdown.total, score);
      this.player = campaignUpdate.data;
      savePlayerData(this.player);
      window.setTimeout(() => {
        this.ui.showCampaignResult(results, score, campaignRank(score), campaignUpdate.totalBest);
        this.ui.setStatus(`Campaign complete. Rank ${campaignRank(score)}.`, true);
      }, prefersReducedMotion(this.player) ? 150 : 850);
    } else {
      savePlayerData(this.player);
      window.setTimeout(() => {
        this.ui.showStageResult(stage, result);
        this.ui.setStatus(`${stage.name} complete. Score ${breakdown.total}.`, true);
      }, prefersReducedMotion(this.player) ? 150 : 720);
    }
  }

  private fail(cause: FailureCause, event?: GameEvent): void {
    if (this.run.phase !== 'playing') return;
    this.run = { ...this.run, phase: 'failed', failure: cause };
    this.ui.setPhase('failed');
    this.ui.setStatus(failureLabel(cause), true);
    if (event) this.dispatch(event);
    window.setTimeout(() => this.ui.showFailure(cause), prefersReducedMotion(this.player) ? 80 : 440);
  }

  private async retry(): Promise<void> {
    if (this.inputLocked || this.run.phase === 'loading' || this.run.phase === 'title') return;
    const retries = this.run.retries + 1;
    this.inputLocked = true;
    this.ui.hideOverlay();
    this.renderer?.resetTrail();
    this.physics?.dispose();
    this.physics = await PhysicsGarden.initialize(getStage(this.run.stageIndex));
    this.run = {
      ...this.run, phase: 'ready', stageScore: 0, combo: 0, maxCombo: 0, pulses: MAX_PULSES, retries,
      elapsed: 0, petalsCollected: 0, lastPetalAt: Number.NEGATIVE_INFINITY, failure: null,
    };
    this.aim = { active: true, vector: { x: Math.cos(this.aimAngle), y: Math.sin(this.aimAngle) }, strength: this.aimStrength };
    this.ui.setPhase('ready'); this.ui.updateHUD(this.run, getStage(this.run.stageIndex)); this.ui.setStatus('Route reset. Pull when ready.', true);
    this.inputLocked = false;
  }

  private async continueCampaign(): Promise<void> {
    if (this.run.phase !== 'stage-complete' || this.inputLocked) return;
    this.ui.hideResult();
    await this.loadStage(this.run.stageIndex + 1);
  }

  private async replay(): Promise<void> {
    if (this.run.phase !== 'campaign-complete' || this.inputLocked) return;
    this.ui.hideResult();
    this.run = createInitialRun('loading');
    await this.loadStage(0);
  }

  private togglePause(): void {
    if (this.run.phase === 'playing') this.pause();
    else if (this.run.phase === 'paused') this.resume();
  }

  private pause(): void {
    if (this.run.phase !== 'playing') return;
    this.run = { ...this.run, phase: 'paused' };
    this.ui.setPhase('paused'); this.ui.showPause(); this.ui.setStatus('Simulation paused', true);
  }

  private resume(): void {
    if (this.run.phase !== 'paused') return;
    this.lastTime = performance.now();
    this.run = { ...this.run, phase: 'playing' };
    this.ui.hideOverlay(); this.ui.setPhase('playing'); this.ui.setStatus('Seed in flight', true);
    this.ui.elements.canvas.focus();
  }

  private readonly onVisibilityChange = (): void => {
    if (document.hidden && this.run.phase === 'playing') this.pause();
  };

  private openHelp(): void {
    if (this.run.phase === 'playing') this.pause();
    if (!this.ui.elements.help.open) this.ui.elements.help.showModal();
  }

  private toggleMute(): void {
    this.player = { ...this.player, muted: !this.player.muted };
    this.audio.setMuted(this.player.muted);
    if (!this.player.muted) void this.audio.activate();
    this.ui.elements.mute.textContent = this.player.muted ? 'Sound off' : 'Sound on';
    this.ui.elements.mute.setAttribute('aria-pressed', String(this.player.muted));
    this.ui.elements.mute.setAttribute('aria-label', this.player.muted ? 'Unmute sound' : 'Mute sound');
    savePlayerData(this.player);
  }

  private cycleMotion(): void {
    this.player = { ...this.player, motion: prefersReducedMotion(this.player) ? 'full' : 'reduced' };
    savePlayerData(this.player);
    this.applyPreferences();
  }

  private applyPreferences(): void {
    const reduced = prefersReducedMotion(this.player);
    document.body.dataset.reducedMotion = String(reduced);
    this.renderer?.setReducedEffects(reduced);
    this.ui.elements.mute.textContent = this.player.muted ? 'Sound off' : 'Sound on';
    this.ui.elements.mute.setAttribute('aria-pressed', String(this.player.muted));
    this.ui.elements.motion.textContent = reduced ? 'Motion low' : 'Motion full';
    this.ui.elements.motion.setAttribute('aria-pressed', String(reduced));
    this.ui.elements.motion.setAttribute('aria-label', reduced ? 'Use full motion' : 'Use reduced motion');
  }

  private dispatch(event: GameEvent): void { this.renderer?.onEvent(event); this.audio.handle(event); }

  private eventWorldPoint(event: PointerEvent): Vec2 {
    const rect = this.ui.elements.canvas.getBoundingClientRect();
    return this.viewport.screenToWorld({ x: event.clientX - rect.left, y: event.clientY - rect.top });
  }

  private showLoadError(error: unknown): void {
    const message = error instanceof Error ? error.message : 'Unknown physics error';
    this.run = { ...this.run, phase: 'loading' };
    this.ui.elements.loading.innerHTML = `<p class="eyebrow">The garden did not wake</p><p class="loading-status">Physics initialization failed.</p><p class="load-error">${escapeHtml(message)}</p><button class="primary-action" id="reload-button" type="button"><span>Try germinating again</span><b aria-hidden="true">Reload</b></button>`;
    document.getElementById('reload-button')?.addEventListener('click', () => window.location.reload());
    this.ui.setStatus('Physics failed to initialize', true);
  }

  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.frame);
    this.physics?.dispose();
    this.audio.dispose();
    this.viewport.dispose();
    this.ui.elements.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.ui.elements.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.ui.elements.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.ui.elements.canvas.removeEventListener('pointercancel', this.cancelDrag);
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    delete window.__KINETIC_BLOOM__;
  }
}

function normalize(vector: Vec2): Vec2 { const length = Math.hypot(vector.x, vector.y) || 1; return { x: vector.x / length, y: vector.y / length }; }
function toward(from: Vec2, to: Vec2): Vec2 { return normalize({ x: to.x - from.x, y: to.y - from.y }); }
function distance(a: Vec2, b: Vec2): number { return Math.hypot(a.x - b.x, a.y - b.y); }
function clamp01(value: number): number { return Math.max(0, Math.min(1, value)); }
function failureLabel(cause: FailureCause): string { return cause === 'thorn' ? 'Caught by thorns' : cause === 'stalled' ? 'Seed came to rest' : 'Seed left the garden'; }
function escapeHtml(value: string): string { const node = document.createElement('span'); node.textContent = value; return node.innerHTML; }
